// src/features/auth/services/biometricService.js
// Biometric authentication service using WebAuthn

import { db } from '@/shared/utils/core/firebase-config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import analytics from '@/shared/utils/analytics/analytics';
import logger from '@/shared/utils/core/logger';


const CREDENTIALS_COLLECTION = 'biometricCredentials';

/**
 * Check if biometric auth is available in current environment
 * @returns {boolean}
 */
export function isBiometricAvailable() {
  // Check if running on HTTPS or localhost
  const isSecure = window.location.protocol === 'https:' ||
                   window.location.hostname === 'localhost' ||
                   window.location.hostname === '127.0.0.1';

  // Check if WebAuthn is supported
  const isWebAuthnSupported = window.PublicKeyCredential !== undefined;

  return isSecure && isWebAuthnSupported;
}

/**
 * Check detailed biometric support
 * @returns {Promise<Object>} - { supported: boolean, reason: string }
 */
export async function checkBiometricSupport() {
  if (!isBiometricAvailable()) {
    return {
      supported: false,
      reason: 'WebAuthn not supported or not on HTTPS'
    };
  }

  try {
    // Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

    return {
      supported: available,
      reason: available ? 'Biometric authentication is available' : 'No platform authenticator found'
    };
  } catch (error) {
    return {
      supported: false,
      reason: error.message
    };
  }
}

/**
 * Get user-friendly device name for biometric auth
 * @returns {string}
 */
export function getBiometricDeviceName() {
  const ua = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) return 'Face ID or Touch ID';
  if (/android/.test(ua)) return 'Fingerprint or Face Unlock';
  if (/windows/.test(ua)) return 'Windows Hello';
  if (/mac/.test(ua)) return 'Touch ID';

  return 'Biometric Authentication';
}

/**
 * Save credential to Firestore
 * @param {string} userId - User ID
 * @param {ArrayBuffer} credentialId - Credential ID
 * @param {ArrayBuffer} publicKey - Public key
 * @returns {Promise<boolean>}
 */
async function saveCredentialId(userId, credentialId, publicKey) {
  try {
    const credentialRef = doc(db, CREDENTIALS_COLLECTION, userId);

    await setDoc(credentialRef, {
      credentialId: Array.from(new Uint8Array(credentialId)),
      publicKey: Array.from(new Uint8Array(publicKey)),
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
      deviceInfo: {
        platform: navigator.platform,
        userAgent: navigator.userAgent.substring(0, 100)
      }
    });

    return true;
  } catch (error) {
    logger.error('Failed to save credential:', error);
    return false;
  }
}

/**
 * Get credential from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>}
 */
async function getCredentialId(userId) {
  try {
    const credentialRef = doc(db, CREDENTIALS_COLLECTION, userId);
    const credentialDoc = await getDoc(credentialRef);

    if (!credentialDoc.exists()) {
      return null;
    }

    const data = credentialDoc.data();

    return {
      credentialId: new Uint8Array(data.credentialId).buffer,
      publicKey: new Uint8Array(data.publicKey).buffer,
      createdAt: data.createdAt,
      lastUsed: data.lastUsed
    };
  } catch (error) {
    logger.error('Failed to get credential:', error);
    return null;
  }
}

/**
 * Update credential last used timestamp
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
async function updateCredentialLastUsed(userId) {
  try {
    const credentialRef = doc(db, CREDENTIALS_COLLECTION, userId);
    await setDoc(credentialRef, {
      lastUsed: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    logger.error('Failed to update credential last used:', error);
  }
}

/**
 * Register biometric credential for user
 * @param {string} userId - User ID
 * @param {string} email - User email
 * @param {string} displayName - User display name
 * @returns {Promise<Object>} - { success: boolean, message?: string, error?: string }
 */
export async function registerBiometric(userId, email, displayName) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const support = await checkBiometricSupport();

    if (!support.supported) {
      throw new Error(support.reason);
    }

    // Check if already registered
    const existing = await getCredentialId(userId);
    if (existing) {
      throw new Error('Biometric already registered. Remove existing registration first.');
    }

    // Generate challenge (in production, this should come from your server)
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);

    // Create credential options
    const publicKeyCredentialCreationOptions = {
      challenge: challenge,
      rp: {
        name: 'Clarity Journal',
        id: window.location.hostname
      },
      user: {
        id: new TextEncoder().encode(userId),
        name: email || displayName || 'User',
        displayName: displayName || email || 'User'
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },  // ES256
        { alg: -257, type: 'public-key' }  // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // Use platform authenticator (Face ID, Touch ID)
        requireResidentKey: false,
        userVerification: 'required'
      },
      timeout: 60000,
      attestation: 'none'
    };

    // Create credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions
    });

    if (!credential) {
      throw new Error('Failed to create credential');
    }

    // Save credential ID to Firestore
    const saved = await saveCredentialId(
      userId,
      credential.rawId,
      credential.response.getPublicKey()
    );

    if (!saved) {
      throw new Error('Failed to save credential');
    }

    // Track analytics
    analytics.trackBiometricRegistration(true);

    return {
      success: true,
      message: 'Biometric authentication registered successfully'
    };

  } catch (error) {
    logger.error('Biometric registration failed:', error);

    // Track analytics
    analytics.trackBiometricRegistration(false, error.message);

    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Authenticate using biometric credential
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { success: boolean, message?: string, error?: string }
 */
export async function authenticateWithBiometric(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required for biometric login');
    }

    const support = await checkBiometricSupport();

    if (!support.supported) {
      throw new Error(support.reason);
    }

    const credential = await getCredentialId(userId);

    if (!credential) {
      throw new Error('No biometric credential registered');
    }

    // Generate challenge
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);

    // Get credential
    const publicKeyCredentialRequestOptions = {
      challenge: challenge,
      allowCredentials: [{
        type: 'public-key',
        id: credential.credentialId,
        transports: ['internal']
      }],
      timeout: 60000,
      userVerification: 'required',
      rpId: window.location.hostname
    };

    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions
    });

    if (!assertion) {
      throw new Error('Authentication failed');
    }

    // Update last used timestamp
    await updateCredentialLastUsed(userId);

    // Track analytics
    analytics.trackBiometricLogin(true);

    return {
      success: true,
      message: 'Biometric authentication successful'
    };

  } catch (error) {
    logger.error('Biometric authentication failed:', error);

    // Track analytics
    analytics.trackBiometricLogin(false, error.message);

    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Remove biometric credential
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { success: boolean, message?: string, error?: string }
 */
export async function removeBiometric(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const credentialRef = doc(db, CREDENTIALS_COLLECTION, userId);
    await setDoc(credentialRef, {
      deleted: true,
      deletedAt: new Date().toISOString()
    }, { merge: true });

    return {
      success: true,
      message: 'Biometric authentication removed'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Check if user has biometric registered
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export async function hasBiometricRegistered(userId) {
  if (!userId) return false;

  try {
    const credential = await getCredentialId(userId);
    return credential !== null && !credential.deleted;
  } catch (error) {
    return false;
  }
}
